import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedFamily — dashboard-embedded family manager.
 *
 * Replaces the recovery FamilyManagementScreen which shipped its own internal
 * "Back to Dashboard" nav, duplicate "My Family" heading, and purple MUI
 * gradient banner. Safety-critical panel — when enabled, Family Mode checks
 * every menu item against every family member's allergies in chat.
 *
 * Data flow: /api/family/members (+ /allergies). Every save goes to the
 * server and the list is re-read afterwards, so what you see is what's stored.
 * Family Mode (stored on this device) makes new chats check every member's
 * allergies too — see dataService.startChat.
 */

const EASE = [0.22, 1, 0.36, 1];

const RELATIONSHIPS = [
  { id: 'spouse',  label: 'Spouse / partner', emoji: 'SP' },
  { id: 'child',   label: 'Child',            emoji: 'CH' },
  { id: 'parent',  label: 'Parent',           emoji: 'PA' },
  { id: 'sibling', label: 'Sibling',          emoji: 'SB' },
  { id: 'grand',   label: 'Grandparent',      emoji: 'GP' },
  { id: 'other',   label: 'Other',            emoji: 'OT' },
];

const COMMON_ALLERGENS = [
  'Peanut', 'Tree nuts', 'Milk', 'Egg', 'Wheat', 'Gluten',
  'Soy', 'Fish', 'Shellfish', 'Sesame', 'Mustard', 'Sulfites',
];

const SEVERITY_LEVELS = [
  { id: 'mild',             label: 'Mild',             level: 0.1, tone: 'amber' },
  { id: 'moderate',         label: 'Moderate',         level: 0.4, tone: 'orange' },
  { id: 'severe',           label: 'Severe',           level: 0.7, tone: 'red' },
  { id: 'life_threatening', label: 'Life-threatening', level: 1.0, tone: 'critical' },
];

const SEVERITY_TONES = {
  amber:    { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  orange:   { bg: '#FFE8D6',                fg: '#8A4A00',               solid: '#EA580C' },
  red:      { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  critical: { bg: '#1F1314',                fg: '#FFE8E8',               solid: '#7F1D1D' },
};

// Pleasant avatar hues that stay legible with white text.
const AVATAR_HUES = [
  { bg: 'linear-gradient(160deg, #1E9BFF, #0F7FE8)', fg: '#fff' },
  { bg: 'linear-gradient(160deg, #FF6B8A, #E91E63)', fg: '#fff' },
  { bg: 'linear-gradient(160deg, #7C3AED, #5B21B6)', fg: '#fff' },
  { bg: 'linear-gradient(160deg, #F59E0B, #D97706)', fg: '#fff' },
  { bg: 'linear-gradient(160deg, #10B981, #059669)', fg: '#fff' },
  { bg: 'linear-gradient(160deg, #EC4899, #BE185D)', fg: '#fff' },
];

function hueFor(name) {
  if (!name) return AVATAR_HUES[0];
  const sum = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_HUES[sum % AVATAR_HUES.length];
}

function levelToSeverity(level) {
  if (level == null) return 'moderate';
  if (level >= 0.85) return 'life_threatening';
  if (level >= 0.55) return 'severe';
  if (level >= 0.25) return 'moderate';
  return 'mild';
}

const emptyMember = () => ({
  id: null,
  name: '',
  age: '',
  relationship: 'child',
  allergies: [], // [{ ingredient, level, severity }]
});

export default function UnifiedFamily({ userId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [familyMode, setFamilyMode] = useState(() => localStorage.getItem('cwm_family_mode') === '1');
  const [dialogMember, setDialogMember] = useState(null); // { ...member, isNew }

  const reload = async () => {
    const data = await dataService.getFamilyMembers();
    setMembers(Array.isArray(data) ? data.map(normalizeMember) : []);
  };

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    dataService.getFamilyMembers()
      .then((data) => { if (!cancelled) setMembers(Array.isArray(data) ? data.map(normalizeMember) : []); })
      .catch(() => { if (!cancelled) setAlert({ tone: 'red', text: "Couldn't load your family. Try again shortly.", id: Date.now() }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    localStorage.setItem('cwm_family_mode', familyMode ? '1' : '0');
  }, [familyMode]);

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const upsertMember = async (data) => {
    setSaving(true);
    try {
      const previous = members.find((m) => m.id === data.id)?.allergies || [];
      await dataService.saveFamilyMember(data, previous);
      flash('green', data.id ? `${data.name} updated.` : `${data.name} added to your family.`);
      setDialogMember(null);
    } catch (e) {
      flash('red', e?.message || `Couldn't save ${data.name}. Try again.`);
    } finally {
      // Re-read even after a partial failure so the list matches the server.
      try { await reload(); } catch { /* keep current list */ }
      setSaving(false);
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.name} from your family?`)) return;
    try {
      await dataService.deleteFamilyMember(member.id);
      flash('red', `${member.name} removed.`);
      await reload();
    } catch (e) {
      flash('red', e?.message || `Couldn't remove ${member.name}.`);
    }
  };

  const totalSevere = useMemo(
    () => members.reduce((n, m) => n + (m.allergies || []).filter((a) => a.severity === 'severe' || a.severity === 'life_threatening').length, 0),
    [members]
  );

  if (loading) return <LoadingBlock />;

  return (
    <div style={{ padding: '18px 20px' }}>

      {/* Alert */}
      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {/* Family Mode toggle */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{
          position: 'relative', overflow: 'hidden',
          padding: '18px 20px',
          borderRadius: 20,
          background: familyMode
            ? 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)'
            : 'linear-gradient(160deg, #F7FBFF 0%, #E5F3FF 100%)',
          border: `1.5px solid ${familyMode ? 'rgba(255,255,255,0.9)' : 'var(--cwm-blue-light)'}`,
          color: familyMode ? '#fff' : 'var(--cwm-ink)',
          marginBottom: 22,
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: familyMode ? '0 16px 32px rgba(13,71,161,0.22)' : 'none',
          transition: 'all 240ms ease',
        }}
      >
        {familyMode && (
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.3,
          }}/>
        )}
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: familyMode ? 'rgba(255,255,255,0.22)' : '#fff',
          color: familyMode ? '#fff' : 'var(--cwm-blue-deep)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
          backdropFilter: familyMode ? 'blur(6px)' : undefined,
          boxShadow: familyMode ? 'inset 0 1px 0 rgba(255,255,255,0.3)' : '0 4px 10px rgba(15,127,232,0.18)',
          zIndex: 1,
        }}>
          <ShieldIcon size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: familyMode ? 'rgba(255,255,255,0.82)' : 'var(--cwm-blue-deep)',
          }}>
            FAMILY MODE {familyMode ? 'ON' : 'OFF'}
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 800, marginTop: 2, lineHeight: 1.4 }}>
            {familyMode
              ? `Chats you open now check every dish against ${members.length || 'your'} family member${members.length === 1 ? "'s" : "s'"} allergies too.`
              : 'Flip on so chat flags unsafe items for everyone at your table, not just you.'}
          </div>
        </div>
        <button
          type="button" onClick={() => setFamilyMode(!familyMode)}
          aria-label="toggle family mode"
          style={{
            width: 56, height: 32, borderRadius: 999,
            background: familyMode ? 'rgba(255,255,255,0.9)' : 'var(--cwm-gray-200)',
            border: 'none', cursor: 'pointer',
            position: 'relative', flexShrink: 0,
            transition: 'background 180ms ease',
            zIndex: 1,
          }}>
          <motion.span
            animate={{ x: familyMode ? 26 : 2 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{
              position: 'absolute', top: 3, left: 0,
              width: 26, height: 26, borderRadius: '50%',
              background: familyMode ? 'var(--cwm-blue-deep)' : '#fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          />
        </button>
      </motion.div>

      {/* Stats row */}
      {members.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 22 }}
        >
          <StatTile label="Members"          value={members.length} />
          <StatTile label="Allergies tracked" value={members.reduce((n, m) => n + (m.allergies?.length || 0), 0)} tone="blue" />
          <StatTile label="Severe or LT"     value={totalSevere} tone={totalSevere > 0 ? 'red' : 'neutral'} />
        </motion.div>
      )}

      {/* Members grid */}
      {members.length === 0 ? (
        <EmptyState onAdd={() => setDialogMember({ ...emptyMember(), isNew: true })} />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="cwm-eyebrow">YOUR FAMILY ({members.length})</div>
            <button type="button"
                    onClick={() => setDialogMember({ ...emptyMember(), isNew: true })}
                    className="cwm-btn"
                    style={{ padding: '8px 16px', fontSize: 13 }}>
              <PlusIcon size={12} /> Add member
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            <AnimatePresence initial={false}>
              {members.map((m, i) => (
                <MemberCard
                  key={m.id || i}
                  member={m}
                  index={i}
                  onEdit={() => setDialogMember({ ...m, isNew: false })}
                  onRemove={() => removeMember(m)}
                />
              ))}
            </AnimatePresence>
          </div>
        </>
      )}

      <AnimatePresence>
        {dialogMember && (
          <MemberDialog
            initial={dialogMember}
            saving={saving}
            onCancel={() => setDialogMember(null)}
            onSave={upsertMember}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ MEMBER CARD ============

function MemberCard({ member, index, onEdit, onRemove }) {
  const hue = hueFor(member.name);
  const relationship = RELATIONSHIPS.find((r) => r.id === member.relationship)?.label || member.relationship || '—';
  const shown = (member.allergies || []).slice(0, 3);
  const extra = Math.max(0, (member.allergies?.length || 0) - shown.length);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 14, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ duration: 0.32, delay: index * 0.04, ease: EASE }}
      whileHover={{ y: -3 }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: hue.bg, color: hue.fg,
          display: 'grid', placeItems: 'center', flexShrink: 0,
          fontSize: 18, fontWeight: 900, letterSpacing: '-0.02em',
          boxShadow: '0 6px 14px rgba(13,71,161,0.18)',
        }}>
          {(member.name || '?').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {member.name}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {relationship}{member.age ? ` · ${member.age}` : ''}
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
          <button type="button" onClick={onEdit} aria-label="edit"
                  style={iconBtn}>
            <EditIcon size={13} />
          </button>
          <button type="button" onClick={onRemove} aria-label="remove"
                  style={iconBtn}>
            <TrashIcon size={13} />
          </button>
        </div>
      </div>

      <div>
        <div className="cwm-eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>
          {member.allergies?.length ? 'ALLERGIES' : 'NO ALLERGIES TRACKED'}
        </div>
        {member.allergies?.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {shown.map((a, i) => {
              const tone = SEVERITY_TONES[SEVERITY_LEVELS.find((s) => s.id === a.severity)?.tone || 'orange'];
              return (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 9px', borderRadius: 999,
                  background: tone.bg, color: tone.fg,
                  fontSize: 11, fontWeight: 900, letterSpacing: '0.02em',
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: tone.solid }}/>
                  {a.ingredient}
                </span>
              );
            })}
            {extra > 0 && (
              <span style={{
                padding: '3px 9px', borderRadius: 999,
                background: 'var(--cwm-gray-100)', color: 'var(--cwm-gray-700)',
                fontSize: 11, fontWeight: 800,
              }}>
                +{extra} more
              </span>
            )}
          </div>
        ) : (
          <button type="button" onClick={onEdit}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                    border: '1.5px dashed var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                    cursor: 'pointer',
                  }}>
            + Add allergies
          </button>
        )}
      </div>
    </motion.article>
  );
}

// ============ DIALOG ============

function MemberDialog({ initial, saving, onCancel, onSave }) {
  const [member, setMember] = useState(initial);
  const [customAllergen, setCustomAllergen] = useState('');
  const [error, setError] = useState('');

  const hasAllergen = (name) => (member.allergies || []).some((a) => a.ingredient.toLowerCase() === name.toLowerCase());

  const addAllergen = (name, severityId = 'moderate') => {
    if (!name.trim()) return;
    if (hasAllergen(name)) return;
    const sev = SEVERITY_LEVELS.find((s) => s.id === severityId) || SEVERITY_LEVELS[1];
    setMember({
      ...member,
      allergies: [...(member.allergies || []), { ingredient: name.trim(), level: sev.level, severity: severityId }],
    });
  };

  const updateAllergenSeverity = (name, severityId) => {
    const sev = SEVERITY_LEVELS.find((s) => s.id === severityId) || SEVERITY_LEVELS[1];
    setMember({
      ...member,
      allergies: (member.allergies || []).map((a) => a.ingredient === name ? { ...a, level: sev.level, severity: severityId } : a),
    });
  };

  const removeAllergen = (name) => setMember({ ...member, allergies: (member.allergies || []).filter((a) => a.ingredient !== name) });

  const submit = () => {
    if (!member.name.trim()) { setError('Give them a name first.'); return; }
    onSave({ ...member, name: member.name.trim(), age: member.age ? String(member.age).trim() : '' });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 24, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--cwm-shadow-hero)' }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
              <FamilyIcon size={12} /> {initial.isNew ? 'ADD FAMILY MEMBER' : 'EDIT MEMBER'}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
              {initial.isNew ? "Who's at your table?" : member.name}
            </h2>
          </div>
          <button onClick={onCancel} aria-label="close"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--cwm-gray-100)', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 900, color: 'var(--cwm-gray-700)' }}>×</button>
        </header>

        <Field label="Name">
          <Input value={member.name} onChange={(v) => setMember({ ...member, name: v })} placeholder="Their name" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 14 }}>
          <Field label="Relationship">
            <SelectPill
              value={member.relationship}
              onChange={(v) => setMember({ ...member, relationship: v })}
              options={RELATIONSHIPS.map((r) => ({ value: r.id, label: r.label }))}
            />
          </Field>
          <Field label="Age (optional)">
            <Input value={member.age} onChange={(v) => setMember({ ...member, age: v })} placeholder="e.g. 8" type="text" />
          </Field>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>QUICK ADD ALLERGENS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {COMMON_ALLERGENS.map((a) => {
              const on = hasAllergen(a);
              return (
                <button key={a} type="button"
                        onClick={() => on ? removeAllergen(a) : addAllergen(a, 'moderate')}
                        style={{
                          padding: '6px 12px', borderRadius: 999,
                          background: on ? '#B91C1C' : '#fff',
                          color: on ? '#fff' : 'var(--cwm-red-text)',
                          border: `1.5px solid ${on ? '#B91C1C' : '#F4B6B6'}`,
                          fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                          cursor: 'pointer',
                          boxShadow: on ? '0 3px 8px rgba(185,28,28,0.3)' : 'none',
                          transition: 'all 140ms ease',
                        }}>
                  {on ? <CheckIcon size={10} /> : <PlusIcon size={10} />} {a}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={customAllergen}
              onChange={(e) => setCustomAllergen(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergen(customAllergen); setCustomAllergen(''); } }}
              placeholder="Other allergen (e.g. lupin, celery)…"
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 12,
                border: '1.5px solid #F4B6B6', background: 'var(--cwm-red-light)',
                color: 'var(--cwm-ink)',
                fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 600,
                outline: 'none',
              }}
            />
            <button type="button" onClick={() => { addAllergen(customAllergen); setCustomAllergen(''); }}
                    disabled={!customAllergen.trim()}
                    style={{
                      padding: '10px 14px', borderRadius: 12,
                      background: customAllergen.trim() ? '#B91C1C' : 'var(--cwm-gray-100)',
                      color: customAllergen.trim() ? '#fff' : 'var(--cwm-gray-500)',
                      border: 'none',
                      fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
                      cursor: customAllergen.trim() ? 'pointer' : 'not-allowed',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
              <PlusIcon size={11} /> Add
            </button>
          </div>
        </div>

        {member.allergies?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 8, color: 'var(--cwm-gray-700)' }}>
              SEVERITY ({member.allergies.length})
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {member.allergies.map((a) => (
                <div key={a.ingredient} style={{
                  padding: 12, borderRadius: 14,
                  background: 'var(--cwm-red-light)',
                  border: '1px solid #F4B6B6',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-red-text)' }}>
                      ⚠ {a.ingredient}
                    </div>
                    <button type="button" onClick={() => removeAllergen(a.ingredient)} aria-label="remove"
                            style={{ background: 'transparent', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', padding: 2 }}>
                      <TrashIcon size={12} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {SEVERITY_LEVELS.map((s) => {
                      const on = s.id === a.severity;
                      const st = SEVERITY_TONES[s.tone];
                      return (
                        <button key={s.id} type="button"
                                onClick={() => updateAllergenSeverity(a.ingredient, s.id)}
                                style={{
                                  padding: '5px 10px', borderRadius: 999,
                                  background: on ? st.solid : st.bg,
                                  color: on ? '#fff' : st.fg,
                                  border: `1.5px solid ${on ? st.solid : 'transparent'}`,
                                  fontFamily: 'var(--cwm-font)', fontSize: 10.5, fontWeight: 900,
                                  cursor: 'pointer', transition: 'all 140ms ease',
                                }}>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <div style={{ fontSize: 13, color: 'var(--cwm-red-text)', fontWeight: 700, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button className="cwm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="cwm-btn" onClick={submit} disabled={saving}>
            {saving ? <Dots /> : initial.isNew ? 'Add member' : 'Save changes'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ SUB COMPONENTS ============

function StatTile({ label, value, tone = 'neutral' }) {
  const palette = {
    neutral: { bg: '#fff',                    fg: 'var(--cwm-ink)',       sub: 'var(--cwm-gray-500)' },
    blue:    { bg: 'var(--cwm-blue-pale)',    fg: 'var(--cwm-blue-deep)', sub: 'var(--cwm-blue-deep)' },
    red:     { bg: 'var(--cwm-red-light)',    fg: 'var(--cwm-red-text)',  sub: 'var(--cwm-red-text)' },
  }[tone];
  return (
    <div className="cwm-sheet" style={{ padding: '14px 16px', background: palette.bg }}>
      <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.sub, opacity: 0.72 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, color: palette.fg, letterSpacing: '-0.02em', marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div style={{
      padding: '48px 24px', textAlign: 'center',
      borderRadius: 22,
      background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
    }}>
      <div style={{
        width: 64, height: 64, margin: '0 auto 14px',
        borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-blue-light)',
      }}>
        <FamilyIcon size={28} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 6px', letterSpacing: '-0.005em' }}>
        No family members yet
      </h3>
      <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--cwm-gray-700)', maxWidth: 400, margin: '0 auto 18px', lineHeight: 1.5 }}>
        Add the people who dine with you. Chat will cross-check every menu against everyone's allergies so no one gets stuck with a boring salad.
      </p>
      <button type="button" onClick={onAdd} className="cwm-btn">
        <PlusIcon size={14} /> Add your first family member
      </button>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, margin: '0 auto 12px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>Loading your family…</div>
    </div>
  );
}

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text' }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF',
      color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
      outline: 'none',
    }} />;
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'block' }}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box',
          appearance: 'none',
          padding: '12px 36px 12px 16px', borderRadius: 14,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#F7FBFF', color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
          cursor: 'pointer', outline: 'none',
        }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

// ============ UTILS ============

// Older (pre-redesign) members were saved with labels like 'Grandparent'.
const RELATIONSHIP_ALIASES = { grandparent: 'grand', partner: 'spouse' };

function normalizeMember(m) {
  const rel = String(m.relationship || 'other').toLowerCase();
  const relationship = RELATIONSHIP_ALIASES[rel] || (RELATIONSHIPS.some((r) => r.id === rel) ? rel : 'other');
  return {
    id: m.id ?? null,
    name: m.name || '',
    age: m.age ?? '',
    relationship,
    allergies: (m.allergies || []).map((a) => {
      const severity = a.severity || levelToSeverity(a.level);
      return {
        id: a.id,
        ingredient: a.ingredient || a.name || '',
        severity,
        level: SEVERITY_LEVELS.find((x) => x.id === severity)?.level ?? 0.4,
      };
    }),
  };
}

const iconBtn = {
  width: 28, height: 28, borderRadius: 999,
  background: '#fff', color: 'var(--cwm-gray-500)',
  border: '1px solid var(--cwm-gray-200)',
  cursor: 'pointer', display: 'grid', placeItems: 'center',
};

// ============ ICONS ============

function ShieldIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/></svg>; }
function FamilyIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="8" r="2.5"/><path d="M3 21v-2a5 5 0 016-5h0a5 5 0 016 5v2M14 14h1a4 4 0 014 4v3"/></svg>; }
function PlusIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function EditIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
